<template>
  <div class="participant-course">
    <v-overlay :value="loading" z-index="4">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <div class="container">
      <v-row>
        <v-col>
          <template v-if="currentCourse || (loading && !currentCourse)">
            <div class="d-flex justify-space-between">
              <v-btn rounded text color="accent" :to="coursesRoute"
                ><v-icon class="mr-2">bhi-arrow-left</v-icon>Back</v-btn
              >
              <v-btn rounded text color="accent" @click="unassignCourse"
                >Unassign</v-btn
              >
            </div>
            <participant-course-info-panel
              v-if="currentCourse"
              :course="currentCourse"
            />
            <main-user-table
              :headers="headers"
              :items="currentCourseSteps"
              :items-per-page="-1"
              :show-all-items="true"
              :server-items-length="-1"
              :page="0"
              :row-component="'participant-course-steps-table-row'"
              :hide-default-footer="true"
              @action:get-data="getDataAction"
              v-if="currentCourseSteps && currentCourseSteps.length"
            >
            </main-user-table>
            <div
              class="d-flex flex-column align-center justify-center text-center pa-6 ma-6"
              v-else
            >
              <h4 class="mb-4">This course don't have steps.</h4>
              <v-btn rounded text color="accent" :to="coursesRoute"
                ><v-icon class="mr-2">bhi-arrow-left</v-icon>Go back to
                courses</v-btn
              >
            </div>
          </template>
          <div
            class="d-flex flex-column align-center justify-center text-center pa-6 ma-6"
            v-else
          >
            <h4 class="mb-4">Course not found.</h4>
            <v-btn rounded text color="accent" :to="coursesRoute"
              ><v-icon class="mr-2">bhi-arrow-left</v-icon>Go back to
              courses</v-btn
            >
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import RouterMixin from '@/mixins/router'
import MainUserTable from '@/components/tables/MainUserTable.vue'
import ParticipantCourseInfoPanel from '@/components/courses/ParticipantCourseInfoPanel.vue'

export default {
  name: 'ParticipantCourse',
  components: {
    MainUserTable,
    ParticipantCourseInfoPanel,
  },
  mixins: [RouterMixin],
  data() {
    return {
      headers: [
        { text: 'Name', value: 'title', sortable: false },
        { text: 'Rating', value: 'rating', sortable: false },
        { text: 'Link', value: 'link', sortable: false },
        { text: 'Finished Date', value: 'finishedDate', sortable: false },
        { text: 'Status', value: 'done', sortable: false },
        { sortable: false },
      ],
    }
  },
  computed: {
    ...mapState('patientsStates', [
      'currentPatient',
      'courses',
      'currentCourse',
      'currentCourseSteps',
      'pagination',
      'loading',
    ]),
    coursesStatus() {
      return this.$route.params?.status || 'ACTIVE'
    },
    isCurrentPageRootLevel() {
      return this.$route.meta.root
    },
    currentParticipantId() {
      return this.$route.params?.id || this.currentPatient?.id || null
    },
    coursesWithCompletePercentage() {
      return this.courses.map(el => {
        const allSteps = el.steps || 0
        const completedSteps = el.stepsCompleted || 0
        const completePercentage =
          allSteps === 0
            ? '0.00'
            : ((completedSteps / allSteps) * 100).toFixed(2)
        return {
          ...el,
          completePercentage,
        }
      })
    },
    coursesRoute() {
      return {
        name: 'Patient Courses',
        path: '/participants/:id/courses',
        params: {
          id: this.currentParticipantId,
          status: this.$route.params?.status,
        },
      }
    },
  },
  beforeRouteLeave: function(to, from, next) {
    this.setBreadcrumbCurrentSubView('')
    this.$store.commit('patientsStates/setCurrentCourse', null)
    this.$store.commit('patientsStates/setCurrentCourseSteps', [])
    next()
  },
  mounted() {
    this.init()
  },
  created() {},
  watch: {
    currentPatient(patient) {
      if (patient?.id) this.init()
    },
    currentCourse(newValue) {
      this.setBreadcrumbCurrentSubView(newValue?.title || '')
    },
    $route(to, from) {
      // get new current patient data when patient id changed
      if (to.params?.id !== undefined && from.params?.id !== to.params?.id) {
        this.getPatient(to.params.id)
      } else if (
        to.params?.courseid !== undefined &&
        from.params?.courseid !== to.params?.courseid &&
        to?.params?.courseid
      ) {
        this.init()
      }
    },
  },
  methods: {
    ...mapActions('patientsStates', [
      'getParticipantCourses',
      'getParticipantCourseSteps',
      'getPatient',
      'unassignParticipantFromCourse',
    ]),
    ...mapMutations('patientsStates', ['setCurrentCourse']),
    ...mapMutations('appStates', ['setBreadcrumbCurrentSubView']),
    async init() {
      const courseId = this.$route.params?.courseid
      if (!this.courses || this.courses.length === 0) {
        await this.getParticipantCourses({
          participantId: this.currentParticipantId,
          status: this.coursesStatus,
        })
      }

      let course = this.currentCourse
      if (course?.id !== courseId) {
        course =
          this.coursesWithCompletePercentage.find(el => el.id === courseId) ||
          null
        this.setCurrentCourse(course)
      }
      if (course?.id) {
        this.getParticipantCourseSteps({
          participantId: this.currentParticipantId,
          courseId: course.id,
        })
      }
      this.setBreadcrumbCurrentSubView(course?.title || 'Not Found')
    },
    unassignCourse() {
      const courseId = this.$route.params?.courseid
      this.unassignParticipantFromCourse({
        participantId: this.currentParticipantId,
        courseId: courseId,
        status: this.coursesStatus,
      })
      this.$router.push(this.coursesRoute)
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.participant-course {
  flex: 1;
  padding: 24px 10px 0;
  height: 100%;
  h3 {
    color: $bh-gray;
    margin-bottom: 10px;
    padding-right: 80px;
    small {
      margin-left: 8px;
      font-size: 0.6em;
      font-weight: 400;
    }
  }
  .v-chip-group {
    margin-bottom: 30px;
    .v-slide-group__content {
      background: $bh-gray--6;
      flex: 0;
      padding-left: 8px;
      border-radius: 20px;
    }
  }
  h2 {
    color: $bh-gray--2;
    font-size: 14px;
  }
  &--wrap {
    height: 100%;
  }
  &--not-found {
    h2 {
      margin-top: 30px;
    }
  }
  & > .container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-bottom: 0;
    & > .row > .col {
      padding-bottom: 0;
    }
  }
  .main-user-table-empty {
    line-height: $gutter * 0.75;
  }
  .v-data-table-header th {
    padding-bottom: $gutter * 0.5 !important;
  }
}
</style>
